import { Badge, Button, Chip, ChipGroup, Stack, TablePager } from '@fukuri-admins/ui/common';
import styles from './styles.module.scss';
import { UsageScopeSelect } from './UsageScopeSelect';
import { UsageTable } from './UsageTable';
import { ViewRateCard } from './ViewRateCard';

type UsersScope = 'at_publication' | 'all';
type ActiveTab = 'unread' | 'read';
type ActivePageTab = 'article' | 'viewRate';

type ArticleFile = {
  name: string;
  url: string;
};

type Props = {
  /** ページタブ（記事 / 閲覧率） */
  activePageTab: ActivePageTab;
  /** ページタブ変更コールバック */
  onPageTabChange: (tab: ActivePageTab) => void;

  /** 記事タブ Props */
  article: {
    /** タイトル */
    title: string;
    /** 本文（改行含む） */
    body: string;
    /** メインビジュアル（null の場合は非表示） */
    mainImageUrl: string | null;
    /** 添付ファイル（空の場合は非表示） */
    files: ArticleFile[];
    /** 公開日時（フォーマット済み文字列） */
    publishAt: string;
    /** 公開済みかどうか（publish_at と現在時刻の比較で導出） */
    isPublished: boolean;
    /** 「編集」ボタンのコールバック */
    onEdit: () => void;
  };

  /** 母数スコープ選択値 */
  usersScope: UsersScope;
  /** 母数スコープ変更コールバック */
  onUsersScopeChange: React.ComponentProps<typeof UsageScopeSelect>['onChange'];

  /** 閲覧率カード Props */
  viewRateCard: React.ComponentProps<typeof ViewRateCard>;

  /** アクティブタブ */
  activeTab: ActiveTab;
  /** 未読ユーザー数 */
  unreadCount: number;
  /** 既読ユーザー数 */
  readCount: number;
  /** タブ変更コールバック */
  onTabChange: (tab: ActiveTab) => void;

  /** ユーザー閲覧状況データ */
  userUsages: React.ComponentProps<typeof UsageTable>['userUsages'];

  /** ページネーション */
  pagerProps: React.ComponentProps<typeof TablePager>;
};

/**
 * 社内報詳細 Presentation コンポーネント
 * 記事内容・閲覧率・閲覧状況テーブルを表示する
 */
export const InternalNewsDetailPresentation: React.FC<Props> = ({
  activePageTab,
  onPageTabChange,
  article,
  usersScope,
  onUsersScopeChange,
  viewRateCard,
  activeTab,
  unreadCount,
  readCount,
  onTabChange,
  userUsages,
  pagerProps,
}) => (
  <Stack gap={16} height='100%'>
    <div className={styles.titleArea}>
      <Badge
        size='lg'
        variant='subtle'
        status={article.isPublished ? 'success' : 'info'}
        label={article.isPublished ? '公開済み' : '公開予定'}
      />
      <p className={styles.newsTitle}>{article.title}</p>
    </div>
    <div className={styles.pageTabList}>
      <button
        type='button'
        className={activePageTab === 'article' ? styles.activePageTab : styles.pageTab}
        onClick={() => onPageTabChange('article')}
      >
        記事
      </button>
      <button
        type='button'
        className={activePageTab === 'viewRate' ? styles.activePageTab : styles.pageTab}
        onClick={() => onPageTabChange('viewRate')}
      >
        閲覧率
      </button>
    </div>
    {activePageTab === 'article' && (
      <div className={styles.articleContent}>
        <Stack gap={24} width='564px'>
          <div className={styles.articleField}>
            <p className={styles.fieldLabel}>タイトル</p>
            <p className={styles.fieldValue}>{article.title}</p>
          </div>
          <div className={styles.articleField}>
            <p className={styles.fieldLabel}>本文</p>
            <p className={styles.fieldValuePreLine}>{article.body}</p>
          </div>
          {article.mainImageUrl !== null && (
            <div className={styles.articleField}>
              <p className={styles.fieldLabel}>メインビジュアル</p>
              <img src={article.mainImageUrl} alt='メインビジュアル' className={styles.mainImage} />
            </div>
          )}
          {article.files.length > 0 && (
            <div className={styles.articleField}>
              <p className={styles.fieldLabel}>ファイル</p>
              <div className={styles.fileList}>
                {article.files.map(file => (
                  <a
                    key={file.url}
                    href={file.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={styles.fileItem}
                  >
                    {file.name}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className={styles.articleField}>
            <p className={styles.fieldLabel}>公開日時</p>
            <p className={styles.fieldValue}>{article.publishAt}</p>
          </div>
        </Stack>
        <div className={styles.articleRight}>
          <Button size='md' onClick={article.onEdit}>
            編集
          </Button>
        </div>
      </div>
    )}
    {activePageTab === 'viewRate' &&
      (article.isPublished ? (
        <>
          <Stack gap={24}>
            <div className={styles.viewRateRow}>
              <ViewRateCard {...viewRateCard} />
              <UsageScopeSelect value={usersScope} onChange={onUsersScopeChange} />
            </div>
            <ChipGroup
              value={activeTab}
              onChange={onTabChange}
              aria-label='閲覧状況'
              className={styles.tabChipGroup}
            >
              <Chip value='unread' size='sm' variant='secondary'>
                未読（{unreadCount}名）
              </Chip>
              <Chip value='read' size='sm' variant='secondary'>
                既読（{readCount}名）
              </Chip>
            </ChipGroup>
          </Stack>
          <UsageTable userUsages={userUsages} />
          <TablePager {...pagerProps} />
        </>
      ) : (
        <div className={styles.viewRateEmpty}>
          <span>記事の公開後に閲覧率を確認できます</span>
        </div>
      ))}
  </Stack>
);
